import React, { useMemo } from 'react';
import styles from './StatisticCard.module.scss';

import InfoButton from '../../../../Atoms/InfoButton/InfoButton';
import moreInfo from '../../../../../assets/icons/programs/major/moreInfo.svg';
import editSvg from '../../../../../assets/icons/formActions/edit.svg';
import deleteSvg from '../../../../../assets/icons/formActions/delete.svg';

const normalizeText = (v) => String(v ?? '').trim();

const pickCountryName = (data, isRtl) =>
  normalizeText(
    (isRtl ? data?.countryName_AR : data?.countryName_EN) ||
      data?.countryName_AR ||
      data?.countryName_EN ||
      ''
  );

const isUrl = (s) => /^https?:\/\//i.test(String(s ?? ''));

const pickFlag = (data) => {
  const raw = normalizeText(data?.flagURL || data?.flagUrl || data?.flag || '');
  if (!raw) return '';
  if (!isUrl(raw) && /^[a-z]{2}$/i.test(raw)) {
    return `https://flagcdn.com/w80/${raw.toLowerCase()}.png`;
  }
  return raw;
};

const pickNumber = (v) => {
  if (v == null) return 0;
  const n = Number(String(v).replace(/[^\d.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const pickCounts = (data) => {
  const bachelorCount = pickNumber(data?.bachelorCount);
  const mastersCount = pickNumber(data?.mastersCount);
  const phdCount = pickNumber(data?.phdCount);
  return { bachelorCount, mastersCount, phdCount };
};

const StatisticsCard = ({
  data,
  onEdit,
  onDelete,
  disabled = false,
  className = '',
  lang = 'ar',
  isRtl = true,
  useArabicDigits = false,
}) => {
  const countryName = useMemo(
    () => pickCountryName(data, isRtl),
    [data, isRtl]
  );
  const flagSrc = useMemo(() => pickFlag(data), [data]);
  const counts = useMemo(() => pickCounts(data), [data]);

  const numberLocale = useMemo(
    () => (useArabicDigits ? lang : `${lang}-u-nu-latn`),
    [lang, useArabicDigits]
  );

  const fmt = useMemo(() => {
    const nf = new Intl.NumberFormat(numberLocale);
    return (n) => nf.format(pickNumber(n));
  }, [numberLocale]);

  const menuItems = useMemo(() => {
    if (disabled) return [];
    return [
      { label: 'تعديل', icon: editSvg, onClick: () => onEdit?.(data) },
      { label: 'حذف', icon: deleteSvg, onClick: () => onDelete?.(data) },
    ];
  }, [disabled, onEdit, onDelete, data]);

  return (
    <div
      className={`${styles.card} ${className}`}
      style={{ overflow: 'visible' }}
      aria-disabled={disabled}
      dir={isRtl ? 'rtl' : 'ltr'}
      lang={lang}
    >
      <div
        className={styles.topRow}
        style={{ overflow: 'visible', position: 'relative' }}
      >
        <div className={styles.flagWrap}>
          {flagSrc ? (
            <img
              className={styles.flag}
              src={flagSrc}
              alt={countryName ? `علم ${countryName}` : 'العلم'}
            />
          ) : (
            <div className={styles.flagFallback} aria-hidden='true' />
          )}
        </div>

        <div style={{ position: 'relative', zIndex: 100 }}>
          <InfoButton
            icon={moreInfo}
            className={styles.moreInfo}
            data={menuItems}
            left
          />
        </div>
      </div>

      <div className={styles.countryName}>{countryName || '-'}</div>

      <div className={styles.rows}>
        <div className={styles.row}>
          <span className={styles.label}>بكالوريوس</span>
          <span className={styles.value} dir='ltr'>
            {fmt(counts.bachelorCount)}
          </span>
        </div>

        <div className={styles.row}>
          <span className={styles.label}>ماجستير</span>
          <span className={styles.value} dir='ltr'>
            {fmt(counts.mastersCount)}
          </span>
        </div>

        <div className={styles.row}>
          <span className={styles.label}>دكتوراه</span>
          <span className={styles.value} dir='ltr'>
            {fmt(counts.phdCount)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default StatisticsCard;
